import { useWorkspaceText, workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useRef, useState } from 'react';
import { Popover } from 'radix-ui';
import { chatHash } from '@/lib/chat-links';
import { useWorkspace } from './context';
import { useNamespaceCopy } from '@/i18n/ns';
import workspaceShellCopy from '@/i18n/ns/workspace-shell';
import { useAppCopy } from '@/i18n/context';
import { useHistorySearch, type HistoryMatch } from './history-search';
import { WorkspaceIcon, type WorkspaceIconName } from './icons';

function recentDate(value: string, uiText: WorkspaceText) {
  const date = new Date(value);
  if (date.toDateString() === new Date().toDateString()) return uiText('Today');
  return new Intl.DateTimeFormat(uiText.locale, { month: 'short', day: 'numeric' }).format(date);
}

const WHERE_ICONS: Record<string, WorkspaceIconName> = {
  private: 'lock',
  members: 'users',
  company: 'globe',
};

// Collaboration v1 (D57 decision 10): one list, and each row says where the
// chat lives — "Only you", "Selected colleagues", "Everyone at …", or whose
// chat it is when it is not yours.

export function recentWhereLabel(
  chat: HistoryMatch,
  companyName: string,
  uiText = workspaceTextFor('en'),
) {
  const where = recentAudienceLabel(chat, companyName, uiText);
  // Projects v1: a chat in a project says so first ("Black Friday 2026 · Only you").
  return chat.project?.name ? `${chat.project.name} · ${where}` : where;
}

function recentAudienceLabel(
  chat: HistoryMatch,
  companyName: string,
  uiText = workspaceTextFor('en'),
) {
  const everyone = uiText('Everyone at {value1}', { value1: companyName });
  if (chat.own !== false)
    return chat.visibility === 'company'
      ? everyone
      : chat.visibility === 'members'
        ? uiText('Selected colleagues')
        : uiText('Only you');
  const owner = chat.ownerName?.split(' ')[0] ?? uiText('A colleague');
  return `${owner} · ${chat.visibility === 'members' ? uiText('shared with you') : everyone}`;
}

// A chat being answered right now: "Anna is getting an answer" (your own:
// "Getting an answer").
export function recentLiveLabel(chat: HistoryMatch, uiText = workspaceTextFor('en')) {
  if (chat.own !== false) return uiText('Getting an answer');
  return uiText('{name} is getting an answer', {
    name: chat.ownerName?.split(' ')[0] ?? uiText('A colleague'),
  });
}

function RecentRow({ chat, onOpen }: { chat: HistoryMatch; onOpen: () => void }) {
  const { text: uiText } = useWorkspaceText();
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceShellCopy);
  const appCopy = useAppCopy();
  return (
    <a
      href={chatHash(chat.threadId)}
      aria-current={ws.threadId === chat.threadId ? 'page' : undefined}
      title={chat.title}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onOpen();
      }}
    >
      {chat.active ? (
        <span className="ws-live-dot" title={copy.beingAnswered} aria-hidden="true" />
      ) : (
        <WorkspaceIcon name="chat" size={16} />
      )}
      <span className="ws-recent-row-text">
        <b>{chat.title}</b>
        {chat.active ? (
          <small className="ws-recent-live">{recentLiveLabel(chat, uiText)}</small>
        ) : (
          <small className="ws-recent-where">
            <WorkspaceIcon name={WHERE_ICONS[chat.visibility ?? 'private']} size={11} />
            {recentWhereLabel(chat, ws.companyName, uiText)}
          </small>
        )}
      </span>
      <small>
        {ws.threadId === chat.threadId ? copy.current : recentDate(chat.updatedAt, uiText)}
      </small>
    </a>
  );
}

export function RecentChats() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(workspaceShellCopy);
  const appCopy = useAppCopy();
  const { recentOpen: open, setRecentOpen: setOpen } = ws;
  const [query, setQuery] = useState('');
  const panel = useRef<HTMLDivElement>(null);
  // One list: your chats, chats shared with you and company chats you opened
  // or followed; typing searches every chat you can read.
  const searching = Boolean(query.trim());
  const history = useHistorySearch(open, query, ws.user?.id, searching ? 'all' : 'recent');
  return (
    <Popover.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <Popover.Trigger
        className="ws-nav-item ws-recent-trigger"
        title={copy.recentChats}
        aria-label={copy.recentChats}
      >
        <WorkspaceIcon name="history" />
        <span>{copy.recentChats}</span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          ref={panel}
          className="ws-recent-panel"
          side={ws.mobileOpen ? 'bottom' : 'right'}
          align="start"
          sideOffset={12}
          collisionPadding={16}
          aria-label={copy.recentChats}
          tabIndex={-1}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            panel.current?.focus();
          }}
        >
          <div className="ws-recent-heading">
            <strong>{copy.pickUp}</strong>
            <small>{copy.recentDescription}</small>
          </div>
          <div className="ws-recent-query">
            <input
              type="search"
              value={query}
              placeholder={copy.searchConversations}
              aria-label={copy.searchConversations}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="ws-recent-list">
            {history.items.slice(0, searching ? 20 : 8).map((chat) => (
              <RecentRow
                key={chat.threadId}
                chat={chat}
                onOpen={() => {
                  setOpen(false);
                  ws.openThread(chat.threadId);
                }}
              />
            ))}
            {history.loading && !history.items.length && <p role="status">{copy.loadingChats}</p>}
            {history.error && (
              <div className="ws-recent-error" role="alert">
                <p>{copy.recentLoadFailed}</p>
                <button onClick={history.retry}>{appCopy.tryAgain}</button>
              </div>
            )}
            {!history.loading && !history.error && !history.items.length && (
              <p>{searching ? copy.noConversationMatch : copy.firstMessage}</p>
            )}
          </div>
          <p className="ws-recent-foot">{copy.agentRunsFooter}</p>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

export function ChatNavigationFeedback() {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  return (
    <div className="ws-chat-navigation-state" role="status">
      {ws.chatStatus === 'loading' ? (
        <p>{uiText('Opening your conversation…')}</p>
      ) : (
        <>
          <WorkspaceIcon name="chat" size={24} />
          <h2>{uiText('This conversation isn’t available')}</h2>
          <p>
            {uiText(
              'It may have been deleted, belong to another account, or be temporarily unavailable.',
            )}
          </p>
          <div className="ws-actions">
            <button className="ws-button" onClick={ws.retryChat}>
              {uiText('Try again')}
            </button>
            <button className="ws-button primary" onClick={ws.newChat}>
              {uiText('Start a new chat')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
